import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  OnDestroy,
  signal,
} from '@angular/core';
import { UiDrawerComponent } from '../../molecules/drawer/drawer.component';
import { UiImageLightboxComponent } from '../image-lightbox/image-lightbox.component';
import { UiImageMosaicGroup } from './image-mosaic.types';

export type { UiImageMosaicGroup };

/**
 * Organismo: mosaico de fotos (1 grande + 2 apiladas) y visor centrado.
 * Presentacional. El clic abre un drawer con grupos (Proyecto / Modelo / Unidad).
 */
@Component({
  selector: 'ui-image-mosaic',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiDrawerComponent, UiImageLightboxComponent],
  templateUrl: './image-mosaic.component.html',
  styleUrl: './image-mosaic.component.scss',
  host: { class: 'ui-image-mosaic-host' },
})
export class UiImageMosaicComponent implements OnDestroy {
  readonly groups = input<readonly UiImageMosaicGroup[]>([]);
  /** Cuántas fotos se ven en el mosaico. El visor sigue usando todos los grupos. */
  readonly maxTiles = input(3);
  readonly title = input('');
  /** Nombre del proyecto. En móvil reemplaza al título completo. */
  readonly project = input('');
  /** Número de unidad. Se muestra debajo del título solo en móvil. */
  readonly unit = input('');
  readonly altPrefix = input('Imagen');

  protected readonly drawerOpen = signal(false);
  protected readonly lightboxOpen = signal(false);
  protected readonly selectedId = signal<string | null>(null);
  protected readonly imageIndex = signal(0);
  protected readonly incomingIndex = signal<number | null>(null);
  protected readonly direction = signal<'next' | 'prev'>('next');
  protected readonly pushing = signal(false);
  private swipeStartX = 0;
  private swipeStartY = 0;
  private stageSwiped = false;
  private pushFrame = 0;

  protected readonly tiles = computed(() =>
    this.groups().slice(0, Math.max(1, this.maxTiles())),
  );

  protected readonly selectedGroup = computed(() => {
    const groups = this.groups();
    const id = this.selectedId() ?? groups[0]?.id;
    return groups.find(group => group.id === id) ?? groups[0] ?? null;
  });

  protected readonly activeImage = computed(() => {
    const group = this.selectedGroup();
    const images = group?.images ?? [];
    if (!images.length) return null;
    return images[this.normalizedIndex()] ?? images[0] ?? null;
  });

  protected readonly canCycle = computed(
    () => (this.selectedGroup()?.images.length ?? 0) > 1,
  );

  protected readonly frames = computed(() => {
    const images = this.selectedGroup()?.images ?? [];
    const currentIndex = this.normalizedIndex();
    const current = images[currentIndex];
    if (!current) return [];
    const incomingIndex = this.incomingIndex();
    const incoming = incomingIndex == null ? null : images[incomingIndex];
    if (!incoming || incomingIndex == null) {
      return [{ src: current, index: currentIndex }];
    }
    const incomingFrame = { src: incoming, index: incomingIndex };
    const currentFrame = { src: current, index: currentIndex };
    return this.direction() === 'prev'
      ? [incomingFrame, currentFrame]
      : [currentFrame, incomingFrame];
  });

  ngOnDestroy(): void {
    cancelAnimationFrame(this.pushFrame);
  }

  protected openGroup(id: string): void {
    this.cancelPush();
    this.selectedId.set(id);
    this.imageIndex.set(0);
    this.drawerOpen.set(true);
  }

  protected selectGroup(id: string): void {
    this.cancelPush();
    this.selectedId.set(id);
    this.imageIndex.set(0);
  }

  protected showPrevious(): void {
    this.pushBy(-1);
  }

  protected showNext(): void {
    this.pushBy(1);
  }

  protected onPushEnd(event: TransitionEvent): void {
    if (
      event.propertyName !== 'transform' ||
      event.target !== event.currentTarget
    ) {
      return;
    }
    const incoming = this.incomingIndex();
    if (incoming == null || !this.pushing()) return;
    this.pushing.set(false);
    this.incomingIndex.set(null);
    this.imageIndex.set(incoming);
  }

  protected onDrawerOpenChange(open: boolean): void {
    this.drawerOpen.set(open);
    if (!open) this.lightboxOpen.set(false);
  }

  protected onStageSwipeStart(event: PointerEvent): void {
    if (!this.isMobileGallery()) return;
    if ((event.target as HTMLElement).closest('.ui-image-mosaic__nav')) return;
    this.swipeStartX = event.clientX;
    this.swipeStartY = event.clientY;
    this.stageSwiped = false;
  }

  protected onStageSwipeCancel(): void {
    this.stageSwiped = false;
  }

  protected onStageSwipeEnd(event: PointerEvent): void {
    if (!this.isMobileGallery()) return;
    if ((event.target as HTMLElement).closest('.ui-image-mosaic__nav')) return;
    const deltaX = event.clientX - this.swipeStartX;
    const deltaY = event.clientY - this.swipeStartY;
    if (Math.abs(deltaX) < 40 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
    this.stageSwiped = true;
    if (deltaX < 0) this.showNext();
    else this.showPrevious();
  }

  protected openLightbox(): void {
    if (this.stageSwiped || this.incomingIndex() !== null) {
      this.stageSwiped = false;
      return;
    }
    if (!this.isMobileGallery()) return;
    if (!this.activeImage()) return;
    this.lightboxOpen.set(true);
  }

  protected tileAlt(group: UiImageMosaicGroup): string {
    return `${this.altPrefix()} ${group.label}`;
  }

  protected normalizedIndex(): number {
    const total = this.selectedGroup()?.images.length ?? 0;
    if (!total) return 0;
    return ((this.imageIndex() % total) + total) % total;
  }

  private isMobileGallery(): boolean {
    return window.matchMedia('(max-width: 1024px)').matches;
  }

  private pushBy(delta: 1 | -1): void {
    const total = this.selectedGroup()?.images.length ?? 0;
    if (total < 2 || this.incomingIndex() !== null) return;
    const next = (this.normalizedIndex() + delta + total) % total;
    cancelAnimationFrame(this.pushFrame);
    this.direction.set(delta > 0 ? 'next' : 'prev');
    this.pushing.set(false);
    this.incomingIndex.set(next);
    this.pushFrame = requestAnimationFrame(() => {
      this.pushFrame = requestAnimationFrame(() => {
        if (this.incomingIndex() == null) return;
        this.pushing.set(true);
      });
    });
  }

  private cancelPush(): void {
    cancelAnimationFrame(this.pushFrame);
    this.pushing.set(false);
    this.incomingIndex.set(null);
  }
}
